// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "app/store"
import {
  convertTimelinePathDateToTimelineDate,
  DateRange,
  getCurrentDate,
  getDatesOfWeek,
  getTimelineDatesInRange,
  isFutureDate,
  isValidTimelinePathDate,
  monthTimelineDates,
  TimelineDate,
  TimelineMonth,
  toDate,
  TODAY_DATE_PARAM,
} from "features/timeline/dateTime"
import * as R from "ramda"
import * as Actions from "../actions"
import {
  IdList,
  PostStatus,
  RemoveFromTimeGroupRequest,
  TimeGroup,
  Timeline,
  TimelineMarker,
  TimeRow,
  TrashStatus,
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import { emptyArray, notUndefined } from "lib/utils"
import { countUnpostedUntrashedUngroupedRows } from "../timeRows"
import { generateTimeZoneInfo, TimeZone } from "../../../app/timeZone"
import { TagRef } from "../../../generated/wisetime/user/type/tag_ref_pb"
import { BASE_PATH, TIMELINE_BASE_PATH } from "app/routing"
import {
  countTrashedRows,
  countUntrashedRows,
  getTimeBlocks,
  getTimeRowsDurationSecs,
  isValidationError,
  TimeBlock,
} from "../timeBlocks"
import { CollapsibleTimeGroup } from "../timeGroups"
import { selectSearchTerms, selectToolbarState, ToolbarState } from "../toolbar/slice"
import { selectUserEmail, selectCurrentExternalId, selectCurrentSiteId, SiteId } from "../../user-context/slice"
import { TagCategoryRef } from "../../../generated/wisetime/user/type/tag_category_ref_pb"
import { TimeGroupsCache, TimeRowsCache } from "../types"
import { selectIsPostingTime } from "./additionalUi"
import { createIndexer, Index } from "./indexer"
import { isMatchingShowSelectedItemToggle, isMatchingToolbarAndSearchFilters } from "../filters"
import { sortTimeGroupsByCreateTime, sortTypeStrategies } from "../sorting"
import { selectTagCategoriesCache, selectTagsCache, TagId } from "../../tags/slice"
import { assignColorsSorted, ColorAssignment, colorFromId, HexColor, modifySimilarColors } from "../color"
import { hasDifferentTagAssignee } from "../../tags/tags"
import { searchSuggestionsCombiner } from "../search-bar/searchUtils"
import * as UserContextSlice from "../../user-context/slice"
import {
  TimesheetField,
  TimesheetFieldConfig,
  TimesheetFieldOption,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { SelectedTimesheetFieldOption } from "../../../generated/wisetime/user/type/selected_timesheet_field_option_pb"

export const createTimesheetFieldCacheKey = (siteId: string, fieldId: string): string =>
  `${siteId}-${fieldId}`

// Avoid putting non-serializable values such as Promises, Symbols, Maps/Sets, functions, or class
// instances into the Redux store state or dispatched actions. This ensures that capabilities such
// as debugging via the Redux DevTools will work as expected. It also ensures that the UI will
// update as expected.
// https://redux.js.org/style-guide/style-guide#do-not-put-non-serializable-values-in-state-or-actions

export type TimeMarkersCache = {
  [id: string]: TimelineMarker.AsObject,
}

export type TimesheetFieldsCache = {
  [timesheetFieldCacheKey: string]: TimesheetField.AsObject,
}

type ConfigHashList = string[]
export type TimeGroupFieldConfigsCache = {
  [timeGroupId: string]: ConfigHashList,
}

export type TimesheetFieldConfigsCache = {
  [configHash: string]: TimesheetFieldConfig.AsObject,
}

export type TimesheetFieldConfigsCacheTimestamps = {
  [configHash: string]: number,
}

export type PendingTimesheetFieldConfigRequests = string[]

export type TimesheetFieldOptionsCache = {
  [id: string]: TimesheetFieldOption.AsObject,
}

export type ActiveTimeGroupIds = {
  timeGroupsIds: string[],
}

export type ActiveTimeRowIds = {
  timeRowIds: string[],
}

export type TimeRowIdsStatus = {
  trashStatus: TrashStatus,
  timeRowIds: string[],
}

export type UpdatingTimeRowsStatusRequest = {
  trashStatus: TrashStatus,
  timeRowIds: string[],
}

// To remember the user selection for each date
export type ActiveTimeGroupsIdsByDate = {
  [date: string]: string[],
}

// To remember the user selection for each date
export type ActiveTimeRowIdsByDate = {
  [date: string]: string[],
}

export type UpdatingTimeRowsStatus = {
  [timeRowId: string]: TrashStatus,
}

// Frontend validation errors before posting to server
export type PostErrorMessages = {
  [id: string]: string,
}

export type TimeRowId = string

export type TimeRowsCacheUpdate = {
  timeRows: TimeRow.AsObject[],
  window: DateRange,
  ofTrashStatus?: TrashStatus,
}

export type AttachTimeRowsTagParams = {
  timeRowIds: IdList,
  tag: TagRef,
}

export type AttachTimeGroupsTagParams = {
  timeGroupsIds: IdList,
  tag: TagRef,
}

export type DetachTimeRowsTagParams = {
  timeRowIds: IdList,
  tagId: string,
}

export type DetachTimeGroupTagParams = {
  timeGroupId: string,
  tagId: string,
}

export type RemoveFromTimeGroupParams = {
  timeGroup: TimeGroup,
  request: RemoveFromTimeGroupRequest,
}

export type SelectRowTimesheetFieldOptionParams = {
  timeRowIds: IdList,
  selectedTimesheetFieldOption: SelectedTimesheetFieldOption.AsObject,
}

export type ClearRowTimesheetFieldParams = {
  timeRowIds: IdList,
  siteId: string,
  timesheetFieldId: string,
}

export enum TimeGroupsDisplayMode {
  EXPANDED = 1,
  COLLAPSED = 2,
}

export enum TimesheetFieldViewMode {
  QUICK = 1,
  BROWSE = 2,
}

const removeFromList = <V>(list: V[], value: V): void => {
  const position = list.findIndex(v => v === value)
  if (position !== -1) {
    list.splice(position, 1)
  }
}

const removeFromKeyedList = <V>(keyedList: { [key: PropertyKey]: V[] }, key: PropertyKey, value: V): void => {
  removeFromList(keyedList[key] || [], value)
  if (keyedList[key]?.length === 0) {
    delete keyedList[key]
  }
}

const keepFields = (transfer: { from: TimeGroup.AsObject, to: TimeGroup.AsObject }): TimeGroup.AsObject => {
  const { from, to } = transfer
  const keepError = isValidationError(from.errorMessage) &&
    from.postStatus === PostStatus.FAILED &&
    to.postStatus === PostStatus.NOT_POSTED &&
    to.errorMessage === ""
  const keepPendingPostStatus = from.postStatus === PostStatus.PENDING &&
    to.postStatus === PostStatus.NOT_POSTED
  if (keepError) {
    return ({
      ...to,
      postStatus: from.postStatus,
      errorMessage: from.errorMessage,
    })
  }
  if (keepPendingPostStatus) {
    return ({
      ...to,
      postStatus: from.postStatus,
    })
  }
  return to
}

const applyKeepFields = (items: TimeGroup.AsObject[], cache: TimeGroupsCache): TimeGroup.AsObject[] =>
  items.map(item => {
    const cachedItem = cache[item.id]
    if (cachedItem === undefined) {
      return item
    }
    return keepFields({ from: cachedItem, to: item })
  })

const timeGroupIsPosted = (timeGroupId: string | undefined, state: TimelinesState): boolean => {
  if (timeGroupId === undefined) {
    return false
  }
  const timeGroup = timeGroupsIndexer.findById(timeGroupId, state)
  if (timeGroup === undefined) {
    return false
  }
  return timeGroup.postStatus === PostStatus.SUCCESS
}

export const getPathStr = () => {
  const { pathname } = window.location
  const slashAtStart = 1
  return pathname.substring(slashAtStart + BASE_PATH.length + TIMELINE_BASE_PATH.length, pathname.length)
}

export const getInitialActiveDate = (): TimelineDate => {
  const pathDateStr = getPathStr()
  if (pathDateStr === TODAY_DATE_PARAM) {
    return getCurrentDate()
  }
  if (isValidTimelinePathDate(pathDateStr)) {
    return convertTimelinePathDateToTimelineDate(pathDateStr)
  }
  return getCurrentDate()
}

const isPostingTime = (timeGroup: TimeGroup.AsObject | undefined): boolean =>
  timeGroup !== undefined && (
    timeGroup.postStatus === PostStatus.PENDING || timeGroup.postStatus === PostStatus.TRANSIENT_FAILURE
  )

export enum Cell {
  TIME_ROWS,
  TAG,
  DURATION,
  NARRATIVE,
}
type KeepTabOrder = {
  [timeGroupId: string]: Cell,
}
type KeepTabOrderPayload = {
  timeGroupId: string,
  cell: Cell,
}
export type TimelinesState = {
  activeDate: TimelineDate,
  currentDay: TimelineDate,
  timeZone?: TimeZone,
  isLoading: boolean,
  refreshRequired: boolean,

  // Time rows from backend.
  timeRowsCache: TimeRowsCache,
  // Time row indexes.
  timeRowsByDateIndex: Index<number>,
  trashedTimeRowsByDateIndex: Index<number>,
  // Time rows UI state.
  activeTimeRowIdsByDate: ActiveTimeRowIdsByDate,
  draggingTimeRowId: string | undefined,
  updatingTimeRowsStatus: UpdatingTimeRowsStatus,

  // Time groups from backend.
  timeGroupsCache: TimeGroupsCache,
  // Time groups index.
  timeGroupsByDateIndex: Index<number>,
  // Time groups UI state.
  activeTimeGroupsIdsByDate: ActiveTimeGroupsIdsByDate,
  failedTimeGroupId?: string, // UpdateTimeGroup RPC failed. In components, reset values then remove failedTimeGroupId.
  timeGroupsDisplayMode: TimeGroupsDisplayMode,
  expandedGroupsSegments: string[],
  collapsedGroupsSegments: string[],
  createRowIndexesList: number[],
  keepTabOrder: KeepTabOrder,

  // Timeline markers from backend.
  timeMarkersCache: TimeMarkersCache,
  timesheetFieldsCache: TimesheetFieldsCache,
  noTimesheetFieldsSiteIdsList: string[],
  timeGroupFieldConfigsCache: TimeGroupFieldConfigsCache,
  // Used for unposted time groups
  timesheetFieldConfigsCache: TimesheetFieldConfigsCache,
  timesheetFieldConfigsCacheTimestamps: TimesheetFieldConfigsCacheTimestamps,
  pendingTimesheetFieldConfigRequests: PendingTimesheetFieldConfigRequests,
  // Used for posted time groups
  timesheetFieldOptionsCache: TimesheetFieldOptionsCache,
  favouriteTimesheetFieldOptionsByField: {
    [timesheetFieldCacheKey: string]: TimesheetFieldOption.AsObject[],
  },
  recentTimesheetFieldOptionsByField: {
    [timesheetFieldCacheKey: string]: TimesheetFieldOption.AsObject[],
  },
  timeMarkersByDateIndex: Index<number>,
  showSelectedTimeRows: boolean,
  timeRowIdsInGroupingProgress: string[],
  timesheetFieldViewMode: TimesheetFieldViewMode,
}

export const initialState: TimelinesState = {
  activeDate: getInitialActiveDate(),
  currentDay: getCurrentDate(),
  timeZone: undefined,
  isLoading: true,
  refreshRequired: true,

  // Time rows from backend.
  timeRowsCache: {},
  // Time row indexes.
  timeRowsByDateIndex: {},
  trashedTimeRowsByDateIndex: {},
  // Time rows UI state.
  activeTimeRowIdsByDate: {},
  draggingTimeRowId: undefined,
  updatingTimeRowsStatus: {},

  // Time groups from backend.
  timeGroupsCache: {},
  // Time groups index.
  timeGroupsByDateIndex: {},
  // Time groups UI state.
  activeTimeGroupsIdsByDate: {},
  failedTimeGroupId: undefined,
  timeGroupsDisplayMode: TimeGroupsDisplayMode.EXPANDED,
  expandedGroupsSegments: [],
  collapsedGroupsSegments: [],
  createRowIndexesList: [0],
  keepTabOrder: {},
  timeMarkersCache: {},
  timesheetFieldsCache: {},
  noTimesheetFieldsSiteIdsList: [],
  timeGroupFieldConfigsCache: {},
  timesheetFieldConfigsCache: {},
  timesheetFieldConfigsCacheTimestamps: {},
  pendingTimesheetFieldConfigRequests: [],
  timesheetFieldOptionsCache: {},
  favouriteTimesheetFieldOptionsByField: {},
  recentTimesheetFieldOptionsByField: {},
  timeMarkersByDateIndex: {},
  showSelectedTimeRows: false,
  timeRowIdsInGroupingProgress: [],
  timesheetFieldViewMode: TimesheetFieldViewMode.QUICK,
}

const timeRowsIndexer = createIndexer<number, TimeRow.AsObject, TimelinesState>({
  selectCache: (state: TimelinesState) => state.timeRowsCache,
  selectIndexes: [
    {
      name: "byDate",
      state: timelineState => timelineState.timeRowsByDateIndex,
      extractKey: timeRow => Number(timeRow.activityDate),
    },
    {
      name: "trashedByDate",
      state: timelineState => timelineState.trashedTimeRowsByDateIndex,
      filter: timeRow => timeRow.trashStatus === TrashStatus.TRASHED,
      extractKey: timeRow => Number(timeRow.activityDate),
    },
  ],
  getId: (timeRow: TimeRow.AsObject) => timeRow.id,
  onItemUpserted: (timeRow: TimeRow.AsObject, state: TimelinesState) => {
    // Only time rows that remain unposted and ungrouped are eligible for active state.
    if (timeRow.timeGroupId !== "") {
      removeFromKeyedList<TimeRowId>(state.activeTimeRowIdsByDate, timeRow.activityDate, timeRow.id)
    }
  },
  onItemsRemoved: (timeRows: TimeRow.AsObject[], state: TimelinesState) => {
    // Clean up secondary state.
    timeRows.forEach(row => {
      removeFromKeyedList<TimeRowId>(state.activeTimeRowIdsByDate, row.activityDate, row.id)
      delete state.updatingTimeRowsStatus[row.id]
    })
  }
})

const timeGroupsIndexer = createIndexer<number, TimeGroup.AsObject, TimelinesState>({
  selectCache: (state: TimelinesState) => state.timeGroupsCache,
  selectIndexes: [{
    name: "byDate",
    state: timelineState => timelineState.timeGroupsByDateIndex,
    extractKey: timeGroup => Number(timeGroup.activityDate),
  }],
  getId: (timeGroup: TimeGroup.AsObject) => timeGroup.id,
  onItemUpserted: (timeGroup: TimeGroup.AsObject, state: TimelinesState) => {
    // Update group relationship for existing time rows.
    timeRowsIndexer.all(state).forEach(row => {
      if (timeGroup.timeRowIdsList.includes(row.id) && row.timeGroupId !== timeGroup.id) {
        row.timeGroupId = timeGroup.id
      } else if (row.timeGroupId === timeGroup.id && !timeGroup.timeRowIdsList.includes(row.id)) {
        row.timeGroupId = ""
      }
    })
  },
  onItemsRemoved: (timeGroups: TimeGroup.AsObject[], state: TimelinesState) => {
    timeGroups.forEach(group => {
      if (state.failedTimeGroupId === group.id) {
        state.failedTimeGroupId = undefined
      }
      removeFromKeyedList(state.activeTimeGroupsIdsByDate, group.activityDate, group.id)
      removeFromList(state.expandedGroupsSegments, group.id)
      removeFromList(state.collapsedGroupsSegments, group.id)
      // Unset group relationship from existing time rows.
      timeRowsIndexer.all(state).forEach(row => {
        if (row.timeGroupId === group.id) {
          row.timeGroupId = ""
        }
      })
    })
  }
})

const timeMarkersIndexer = createIndexer<number, TimelineMarker.AsObject, TimelinesState>({
  selectCache: (state: TimelinesState) => state.timeMarkersCache,
  selectIndexes: [{
    name: "byDate",
    state: timelineState => timelineState.timeMarkersByDateIndex,
    extractKey: timeMarker => Number(timeMarker.date),
  }],
  getId: timeMarker => timeMarker.id,
})

const isTimelineCached = (forDate: TimelineDate, state: TimelinesState): boolean =>
  timeRowsIndexer.findByIndex("byDate", forDate, state).length > 0
  || timeGroupsIndexer.findByIndex("byDate", forDate, state).length > 0
  || timeMarkersIndexer.findByIndex("byDate", forDate, state).length > 0

export const slice = createSlice({
  name: "timeline",
  initialState,
  reducers: {
    setActiveDate: (state, action: PayloadAction<TimelineDate>): void => {
      state.activeDate = action.payload
      state.refreshRequired = true
      if (state.timeZone && state.timeZone.name) {
        state.timeZone = generateTimeZoneInfo(state.timeZone.name, action.payload)
      }
    },
    setCurrentDay: (state, action: PayloadAction<TimelineDate>): void => {
      state.currentDay = action.payload
    },
    setTimeline: (state, action: PayloadAction<Timeline.AsObject[]>): void => {
      action.payload.forEach(timeline => {
        const timelineDate = Number(timeline.date)
        // While one date tab is loading, users may quickly click another date tab, end up with a
        // different activeDate and loadedTimelineDate. Therefore, here we set 'refreshRequired' only
        // when the activeDate matches the timeline that is being set.
        if (state.activeDate === timelineDate) {
          state.refreshRequired = false
          state.isLoading = false
        }

        const timeGroupsInRange = applyKeepFields(
          timeline.timeGroupsInRangeList,
          timeGroupsIndexer.cache(state),
        )

        // Entities in range can be used to replace indexed items and purge stale items from the cache.
        timeRowsIndexer.replaceForIndex("byDate", timelineDate, timeline.timeRowsInRangeList, state)
        timeGroupsIndexer.replaceForIndex("byDate", timelineDate, timeGroupsInRange, state)
        timeMarkersIndexer.replaceForIndex("byDate", timelineDate, timeline.markersList, state)
      })
    },
    upsertTimeGroup: (state, action: PayloadAction<TimeGroup.AsObject>): void => {
      const cachedTimeGroup = timeGroupsIndexer.findById(action.payload.id, state)
      if (cachedTimeGroup) {
        timeGroupsIndexer.upsert(keepFields({
          from: cachedTimeGroup,
          to: action.payload,
        }), state)
      } else {
        timeGroupsIndexer.upsert(action.payload, state)
      }
    },
    deleteTimeGroups: (state, action: PayloadAction<string[]>): void => {
      action.payload.forEach(timeGroupId => {
        timeGroupsIndexer.remove(timeGroupId, state)
      })
    },
    // This reducer will not purge stale time rows since we don't have an update window.
    setTimeRows: (state, action: PayloadAction<TimeRow.AsObject[]>): void => {
      action.payload.forEach(timeRow => {
        timeRowsIndexer.upsert(timeRow, state)
      })
    },
    // Use this reducer to purge stale time rows using an update window.
    setTimeRowsForDates: (state, action: PayloadAction<TimeRowsCacheUpdate>): void => {
      const { startDateLocal, endDateLocal } = action.payload.window
      const { timeRows, ofTrashStatus } = action.payload
      const timeRowsByDate = (date: TimelineDate) => ({
        date,
        timeRows: timeRows.filter(r => date === Number(r.activityDate)),
      })
      getTimelineDatesInRange(startDateLocal, endDateLocal)
        .map(timeRowsByDate)
        .forEach(update => {
          const { date, timeRows } = update
          timeRowsIndexer.replaceForIndex(
            ofTrashStatus === TrashStatus.TRASHED ? "trashedByDate" : "byDate",
            date,
            timeRows,
            state,
          )
        })
    },
    replaceActiveTimeGroupsIds: (state, action: PayloadAction<ActiveTimeGroupIds>): void => {
      state.activeTimeGroupsIdsByDate[state.activeDate] = action.payload.timeGroupsIds
    },
    setActiveTimeRowIds: (state, action: PayloadAction<ActiveTimeRowIds>): void => {
      const date = state.activeDate
      const activeTimeGroupsIds = state.activeTimeGroupsIdsByDate[date]
      if (activeTimeGroupsIds !== undefined && timeGroupIsPosted(activeTimeGroupsIds[0], state)) {
        delete state.activeTimeGroupsIdsByDate[date]
      }
      const activeTimeRowIds = action.payload

      if (activeTimeRowIds.timeRowIds.length > 0) {
        state.activeTimeRowIdsByDate[date] = activeTimeRowIds.timeRowIds
      } else {
        delete state.activeTimeRowIdsByDate[date]
        state.showSelectedTimeRows = false
      }
    },
    addActiveTimeRowIds: (state, action: PayloadAction<ActiveTimeRowIds>): void => {
      const { activeDate } = state
      const timeRowIdsByDate = state.activeTimeRowIdsByDate[activeDate] ?? []
      action.payload.timeRowIds.forEach(timeRowId => {
        if (!timeRowIdsByDate.includes(timeRowId)) {
          timeRowIdsByDate.push(timeRowId)
        }
      })
      state.activeTimeRowIdsByDate[activeDate] = timeRowIdsByDate
    },
    addUnpostedActiveTimeGroupId: (state, action: PayloadAction<string>): void => {
      const activeTimeGroupsIds = state.activeTimeGroupsIdsByDate[state.activeDate]
      const hasPostedTimeGroups = (activeTimeGroupsIds ?? [])
        .map(id => timeGroupsIndexer.findById(id, state))
        .filter(notUndefined)
        .some(({ postStatus }) => (
          postStatus === PostStatus.SUCCESS
          || postStatus === PostStatus.PENDING
          || postStatus === PostStatus.TRANSIENT_FAILURE
        ))
      if (activeTimeGroupsIds === undefined || hasPostedTimeGroups) {
        state.activeTimeGroupsIdsByDate[state.activeDate] = [action.payload]
        return
      }
      activeTimeGroupsIds.push(action.payload)
    },
    removeActiveTimeGroupsIds: (state, action: PayloadAction<ActiveTimeGroupIds>): void => {
      const { timeGroupsIds } = action.payload
      const activeTimeGroupsIds = state.activeTimeGroupsIdsByDate[state.activeDate]
      if (activeTimeGroupsIds === undefined) {
        return
      }
      state.activeTimeGroupsIdsByDate[state.activeDate] = activeTimeGroupsIds.filter(id => !timeGroupsIds.includes(id))
    },
    removeActiveTimeRowIds: (state, action: PayloadAction<ActiveTimeRowIds>): void => {
      const activeTimeRowIds = action.payload
      const date = state.activeDate
      const timeRowIdsByDate = state.activeTimeRowIdsByDate[date] || []

      activeTimeRowIds.timeRowIds.forEach(timeRowId => {
        const foundIndex = timeRowIdsByDate.findIndex(x => x == timeRowId)
        if (foundIndex > -1) {
          timeRowIdsByDate.splice(foundIndex, 1)
        }
      })
      state.activeTimeRowIdsByDate[date] = timeRowIdsByDate
      if (timeRowIdsByDate.length === 0) {
        state.showSelectedTimeRows = false
      }
    },
    updateTimeRowsStatus: (state, action: PayloadAction<TimeRowIdsStatus>): void => {
      action.payload.timeRowIds.forEach(timeRowId => {
        const row = timeRowsIndexer.findById(timeRowId, state)
        if (row === undefined) {
          return
        }
        row.trashStatus = action.payload.trashStatus
      })
    },
    setUpdatingTimeRowsStatus: (state, action: PayloadAction<UpdatingTimeRowsStatusRequest>): void => {
      const { timeRowIds, trashStatus } = action.payload
      timeRowIds.forEach(id => {
        state.updatingTimeRowsStatus[id] = trashStatus
      })
    },
    removeUpdatingTimeRowsStatus: (state, action: PayloadAction<TimeRowId[]>): void => {
      action.payload.forEach(timeRowId => {
        delete state.updatingTimeRowsStatus[timeRowId]
      })
    },
    removeAllTrashedTimeRows: (state): void => {
      timeRowsIndexer.all(state).forEach(timeRow => {
        if (timeRow.trashStatus === TrashStatus.TRASHED) {
          timeRowsIndexer.remove(timeRow.id, state)
        }
      })
    },
    deleteTimeRows: (state, action: PayloadAction<string[]>): void => {
      action.payload.forEach(timeRowId => {
        timeRowsIndexer.remove(timeRowId, state)
      })
    },
    setLoading: (state, action: PayloadAction<boolean>): void => {
      state.isLoading = action.payload
    },
    setRefreshRequired: (state, action: PayloadAction<boolean>): void => {
      state.refreshRequired = action.payload
    },
    resetLoadingAndRefreshRequired: (state): void => {
      state.isLoading = false
      state.refreshRequired = false
    },
    setTimeGroupsPostErrorMessages: (state, action: PayloadAction<PostErrorMessages>): void => {
      const postErrorMessages = action.payload
      Object.keys(postErrorMessages)
        .forEach(id => {
          const timeGroup = timeGroupsIndexer.findById(id, state)
          const errorMessage = postErrorMessages[id]
          if (!timeGroup || !errorMessage) {
            return
          }
          timeGroup.postStatus = PostStatus.FAILED
          timeGroup.errorMessage = errorMessage
        })
    },
    clearTimeGroupsPostErrors: (state, action: PayloadAction<string[]>): void => {
      action.payload.forEach(id => {
        const timeGroup = timeGroupsIndexer.findById(id, state)
        if (timeGroup) {
          timeGroup.postStatus = PostStatus.NOT_POSTED
          timeGroup.errorMessage = ""
        }
      })
    },
    attachTimeRowsTag: (state, action: PayloadAction<AttachTimeRowsTagParams>): void => {
      action.payload.timeRowIds.getIdsList().forEach(id => {
        const cachedTimeRow = timeRowsIndexer.findById(id, state)
        if (!cachedTimeRow) {
          return
        }
        const attachedTag = action.payload.tag
        if (!cachedTimeRow.tagsList.some(tag => tag.id === attachedTag.getId())) {
          cachedTimeRow.tagsList.push(attachedTag.toObject())
        }
        cachedTimeRow.tagSuggestionsList = cachedTimeRow
          .tagSuggestionsList.filter(suggestion => suggestion.tag?.siteId !== attachedTag.getSiteId())
      })
    },
    attachTimeGroupsTags: (state, action: PayloadAction<AttachTimeGroupsTagParams>): void => {
      action.payload.timeGroupsIds.getIdsList().map(id => timeGroupsIndexer.findById(id, state))
        .filter(notUndefined)
        .forEach(timeGroup => timeGroup.tagsList.push(action.payload.tag.toObject()))
    },
    updateRowTimesheetFieldOption: (state, action: PayloadAction<SelectRowTimesheetFieldOptionParams>): void => {
      action.payload.timeRowIds.getIdsList().forEach(id => {
        const cachedTimeRow = timeRowsIndexer.findById(id, state)
        if (!cachedTimeRow) {
          return
        }
        const { selectedTimesheetFieldOption } = action.payload
        const existingOptionIndex = cachedTimeRow.selectedTimesheetFieldOptionsList
          .findIndex(option => 
            option.siteId === selectedTimesheetFieldOption.siteId &&
            option.timesheetFieldId === selectedTimesheetFieldOption.timesheetFieldId
          )
        if (existingOptionIndex !== -1) {
          cachedTimeRow.selectedTimesheetFieldOptionsList[existingOptionIndex] = selectedTimesheetFieldOption
        } else {
          cachedTimeRow.selectedTimesheetFieldOptionsList.push(selectedTimesheetFieldOption)
        }
      })
    },
    clearRowTimesheetField: (state, action: PayloadAction<ClearRowTimesheetFieldParams>): void => {
      action.payload.timeRowIds.getIdsList().forEach(id => {
        const cachedTimeRow = timeRowsIndexer.findById(id, state)
        if (!cachedTimeRow) {
          return
        }
        const { siteId, timesheetFieldId } = action.payload
        cachedTimeRow.selectedTimesheetFieldOptionsList = cachedTimeRow.selectedTimesheetFieldOptionsList
          .filter(option => !(option.siteId === siteId && option.timesheetFieldId === timesheetFieldId))
      })
    },
    detachTimeRowsTag: (state, action: PayloadAction<DetachTimeRowsTagParams>): void => {
      action.payload.timeRowIds.getIdsList().forEach(id => {
        const timeRow = timeRowsIndexer.findById(id, state)
        if (timeRow) {
          timeRow.tagsList = timeRow.tagsList.filter(tag => tag.id !== action.payload.tagId)
        }
      })
    },
    detachTimeGroupTag: (state, action: PayloadAction<DetachTimeGroupTagParams>): void => {
      const timeGroup = timeGroupsIndexer.findById(action.payload.timeGroupId, state)
      if (!timeGroup) {
        return
      }
      timeGroup.tagsList = timeGroup.tagsList.filter(tag => tag.id !== action.payload.tagId)
    },
    removeFromTimeGroup: (state, action: PayloadAction<RemoveFromTimeGroupParams>): void => {
      const timeGroup = timeGroupsIndexer.findById(action.payload.request.getTimeGroupId(), state)
      if (!timeGroup) {
        return
      }
      const timeRowIdsToRemove = action.payload.request.getTimeRowIdsList()
      timeRowIdsToRemove.forEach(id => {
        const timeRow = timeRowsIndexer.findById(id, state)
        if (timeRow !== undefined) {
          timeRow.timeGroupId = ""
        }
      })
      timeGroupsIndexer.upsert(action.payload.timeGroup.toObject(), state)
    },
    detachTagCategory: (state, action: PayloadAction<{
      timeRowIds: string[] | undefined,
      timeGroupId: string,
      tagCategoryId: string,
    }>) => {
      const removeTagCategory = ({ id }: TagCategoryRef.AsObject): boolean => id !== action.payload.tagCategoryId
      const timeGroup = timeGroupsIndexer.findById(action.payload.timeGroupId, state)
      if (timeGroup !== undefined) {
        timeGroup.tagCategoriesList = timeGroup.tagCategoriesList.filter(removeTagCategory)
      }
      if (action.payload.timeRowIds !== undefined) {
        action.payload.timeRowIds.forEach(timeRowId => {
          const timeRow = timeRowsIndexer.findById(timeRowId, state)
          if (timeRow !== undefined) {
            timeRow.tagCategoriesList = timeRow.tagCategoriesList.filter(removeTagCategory)
          }
        })
      }
    },
    setTimeGroupsDisplayMode: (state, action: PayloadAction<TimeGroupsDisplayMode>) => {
      state.timeGroupsDisplayMode = action.payload
      state.expandedGroupsSegments = []
      state.collapsedGroupsSegments = []
    },
    addExpandedGroupSegment: (state, action: PayloadAction<string>) => {
      if (!state.expandedGroupsSegments.includes(action.payload)) {
        state.expandedGroupsSegments.push(action.payload)
      }
    },
    removeExpandedGroupSegment: (state, action: PayloadAction<string>) => {
      const index = state.expandedGroupsSegments.indexOf(action.payload)
      if (index > -1) {
        state.expandedGroupsSegments.splice(index, 1)
      }
    },
    addCollapsedGroupSegment: (state, action: PayloadAction<string>) => {
      if (!state.collapsedGroupsSegments.includes(action.payload)) {
        state.collapsedGroupsSegments.push(action.payload)
      }
    },
    removeCollapsedGroupSegment: (state, action: PayloadAction<string>) => {
      const index = state.collapsedGroupsSegments.indexOf(action.payload)
      if (index > -1) {
        state.collapsedGroupsSegments.splice(index, 1)
      }
    },
    removeFromCreateRowIndexesList: (state, action: PayloadAction<number>) => {
      const index = state.createRowIndexesList.indexOf(action.payload)
      if (index > -1) {
        state.createRowIndexesList.splice(index, 1)
      }
    },
    setFailedTimeGroupId: (state, action: PayloadAction<string>) => {
      state.failedTimeGroupId = action.payload
    },
    resetFailedTimeGroupId: (state) => {
      delete state.failedTimeGroupId
    },
    addTimelineMarker: (state, action: PayloadAction<TimelineMarker.AsObject>) => {
      timeMarkersIndexer.upsert(action.payload, state)
    },
    deleteTimelineMarker: (state, action: PayloadAction<string>) => {
      timeMarkersIndexer.remove(action.payload, state)
    },
    setShowSelectedTimeRows: (state, action: PayloadAction<boolean>) => {
      state.showSelectedTimeRows = action.payload
    },
    addTimeRowIdsInGroupingProgress: (state, action: PayloadAction<string[]>) => {
      state.timeRowIdsInGroupingProgress = state.timeRowIdsInGroupingProgress.concat(action.payload)
    },
    removeTimeRowIdsInGroupingProgress: (state, action: PayloadAction<string[]>) => {
      state.timeRowIdsInGroupingProgress = state.timeRowIdsInGroupingProgress
        .filter(item => !action.payload.includes(item))
    },
    setDraggingTimeRowId: (state, action: PayloadAction<string | undefined>) => {
      state.draggingTimeRowId = action.payload
    },
    setTimesheetFields: (state, action: PayloadAction<{
      siteId: string,
      timesheetFields: TimesheetField.AsObject[],
    }>) => {
      const { siteId, timesheetFields } = action.payload
      const hasTimesheetFields = timesheetFields.length > 0
      const isInNoFieldsList = state.noTimesheetFieldsSiteIdsList.includes(siteId)
      
      // Add to noTimesheetFieldsSiteIdsList if no fields and not already present
      if (!hasTimesheetFields && !isInNoFieldsList) {
        state.noTimesheetFieldsSiteIdsList.push(siteId)
      }
      
      // Remove from noTimesheetFieldsSiteIdsList if has fields and currently present
      if (hasTimesheetFields && isInNoFieldsList) {
        state.noTimesheetFieldsSiteIdsList.splice(state.noTimesheetFieldsSiteIdsList.indexOf(siteId), 1)
      }
      
      // Update cache with timesheet fields
      timesheetFields.forEach(timesheetField => {
        const cacheKey = createTimesheetFieldCacheKey(timesheetField.siteId, timesheetField.id)
        state.timesheetFieldsCache[cacheKey] = timesheetField
      })

      // Clear stale timesheet fields for this site that are not in the new payload
      const newFieldCacheKeys = new Set(
        timesheetFields.map(field => createTimesheetFieldCacheKey(field.siteId, field.id))
      )
      Object.keys(state.timesheetFieldsCache).forEach(cacheKey => {
        const cachedField = state.timesheetFieldsCache[cacheKey]
        if (cachedField?.siteId === siteId && !newFieldCacheKeys.has(cacheKey)) {
          delete state.timesheetFieldsCache[cacheKey]
        }
      })
    },
    setTimesheetField: (state, action: PayloadAction<TimesheetField.AsObject>) => {
      const cacheKey = createTimesheetFieldCacheKey(
        action.payload.siteId,
        action.payload.id,
      )
      state.timesheetFieldsCache[cacheKey] = action.payload
    },
    deleteTimesheetField: (state, action: PayloadAction<{
      id: string,
      siteId: string,
    }>) => {
      const cacheKey = createTimesheetFieldCacheKey(
        action.payload.siteId,
        action.payload.id,
      )
      delete state.timesheetFieldsCache[cacheKey]
    },
    setTimeGroupFieldConfigs: (state, action: PayloadAction<{
      timeGroupId: string,
      configHashList: ConfigHashList,
    }>) => {
      state.timeGroupFieldConfigsCache[action.payload.timeGroupId] = action.payload.configHashList
    },
    setTimesheetFieldConfigs: (state, action: PayloadAction<TimesheetFieldConfig.AsObject[]>) => {
      const currentTimestamp = Date.now()
      action.payload.forEach(timesheetFieldConfig => {
        const hash = timesheetFieldConfig?.configRef?.configHash
        if (hash) {
          state.timesheetFieldConfigsCache[hash] = timesheetFieldConfig
          state.timesheetFieldConfigsCacheTimestamps[hash] = currentTimestamp
          // Clear pending request for this config hash
          const index = state.pendingTimesheetFieldConfigRequests.indexOf(hash)
          if (index > -1) {
            state.pendingTimesheetFieldConfigRequests.splice(index, 1)
          }
        }
      })
    },
    setTimesheetFieldOptions: (state, action: PayloadAction<TimesheetFieldOption.AsObject[]>) => {
      action.payload.forEach(timesheetFieldOption => {
        state.timesheetFieldOptionsCache[timesheetFieldOption.id] = timesheetFieldOption
      })
    },
    setRecentTimesheetFieldOptions: (
      state,
      action: PayloadAction<{ siteId: string, options: TimesheetFieldOption.AsObject[] }>,
    ) => {
      const { siteId, options } = action.payload
      const groupedOptions = options.reduce((acc, opt) => {
        const cacheKey = createTimesheetFieldCacheKey(siteId, opt.timesheetFieldId)
        acc[cacheKey] = [...(acc[cacheKey] || []), opt]
        return acc
      }, {} as Record<string, TimesheetFieldOption.AsObject[]>)
      
      state.recentTimesheetFieldOptionsByField = {
        ...state.recentTimesheetFieldOptionsByField,
        ...groupedOptions
      }
    },
    setFavouriteTimesheetFieldOptions: (
      state,
      action: PayloadAction<{ siteId: string, options: TimesheetFieldOption.AsObject[] }>,
    ) => {
      const { siteId, options } = action.payload

      if (options.length === 0) {
        // When no options, remove all existing entries for this site
        const filteredFavourites = Object.fromEntries(
          Object.entries(state.favouriteTimesheetFieldOptionsByField)
            .filter(([key]) => !key.startsWith(`${siteId}-`))
        )
        state.favouriteTimesheetFieldOptionsByField = filteredFavourites
        return
      }

      const groupedOptions = options.reduce((acc, opt) => {
        const cacheKey = createTimesheetFieldCacheKey(siteId, opt.timesheetFieldId)
        acc[cacheKey] = [...(acc[cacheKey] || []), opt]
        return acc
      }, {} as Record<string, TimesheetFieldOption.AsObject[]>)
      
      state.favouriteTimesheetFieldOptionsByField = {
        ...state.favouriteTimesheetFieldOptionsByField,
        ...groupedOptions
      }
    },
    setTimesheetFieldViewMode: (state, action: PayloadAction<TimesheetFieldViewMode>): void => {
      state.timesheetFieldViewMode = action.payload
    },
    setKeepTabOrder: (state, action: PayloadAction<KeepTabOrderPayload>) => {
      state.keepTabOrder[action.payload.timeGroupId] = action.payload.cell
    },
    removeKeepTabOrder: (state, action: PayloadAction<string>) => {
      delete state.keepTabOrder[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(Actions.setTimeZone, (state, action) => {
        state.timeZone = action.payload
      })
      .addCase(Actions.createEmptyTimeGroup, (state, action) => {
        if (action.payload.createTimeGroupRowIndex !== undefined) {
          const nextIndex = action.payload.createTimeGroupRowIndex + 1
          state.createRowIndexesList = [...state.createRowIndexesList, nextIndex]
        }
      })
      .addCase(Actions.getTimelineData, (state, action) => {
        // Figure out whether we need to set isLoading state while fetching timeline data.
        const requestedTimelineDate = action.payload ? action.payload : state.activeDate
        const isCached = isTimelineCached(requestedTimelineDate, state)
        if (isFutureDate(state.activeDate)) {
          state.isLoading = false
        } else if (!isCached && requestedTimelineDate === state.activeDate) {
          state.isLoading = true
        }
      })
      .addCase(Actions.postTime, (state, action) => {
        // Immediately set post status as pending to let user know that something is happening.
        // Next status change will be when post time response is back. After that, subsequent
        // status changes will be notified via WatchTimelineUpdates. Time posting is async if
        // we are posting to a site that has a connector.
        action.payload.timeGroups.map(t => t.id).forEach(id => {
          const timeGroup = timeGroupsIndexer.findById(id, state)
          if (timeGroup) {
            timeGroup.postStatus = PostStatus.PENDING
          }
        })
      })
      .addCase(UserContextSlice.setCurrentSiteId, (state) => {
        state.timeGroupFieldConfigsCache = initialState.timeGroupFieldConfigsCache
      })
      .addCase(Actions.batchGetTimesheetFieldConfigs, (state, action) => {
        // Add config hashes to pending requests when the action is dispatched
        action.payload.getConfigRefsList().forEach(configRef => {
          const configHash = configRef.getConfigHash()
          if (!state.pendingTimesheetFieldConfigRequests.includes(configHash)) {
            state.pendingTimesheetFieldConfigRequests.push(configHash)
          }
        })
      })
      .addCase(Actions.batchGetTimesheetFieldConfigsFailed, (state, action) => {
        // Clear pending requests for failed config hashes
        action.payload.getConfigRefsList().forEach(configRef => {
          const configHash = configRef.getConfigHash()
          const index = state.pendingTimesheetFieldConfigRequests.indexOf(configHash)
          if (index > -1) {
            state.pendingTimesheetFieldConfigRequests.splice(index, 1)
          }
        })
      })
  },
})

export const {
  setActiveDate,
  setCurrentDay,
  setTimeline,
  upsertTimeGroup,
  setTimeRows,
  setTimeRowsForDates,
  replaceActiveTimeGroupsIds,
  setActiveTimeRowIds,
  addActiveTimeRowIds,
  deleteTimeRows,
  removeActiveTimeRowIds,
  removeAllTrashedTimeRows,
  updateTimeRowsStatus,
  setUpdatingTimeRowsStatus,
  removeUpdatingTimeRowsStatus,
  setLoading,
  setRefreshRequired,
  resetLoadingAndRefreshRequired,
  setTimeGroupsPostErrorMessages,
  clearTimeGroupsPostErrors,
  deleteTimeGroups,
  detachTimeRowsTag,
  detachTimeGroupTag,
  removeFromTimeGroup,
  attachTimeRowsTag,
  attachTimeGroupsTags,
  addUnpostedActiveTimeGroupId,
  removeActiveTimeGroupsIds,
  updateRowTimesheetFieldOption,
  clearRowTimesheetField,
  detachTagCategory,
  setTimeGroupsDisplayMode,
  addExpandedGroupSegment,
  removeExpandedGroupSegment,
  addCollapsedGroupSegment,
  removeCollapsedGroupSegment,
  removeFromCreateRowIndexesList,
  setFailedTimeGroupId,
  resetFailedTimeGroupId,
  addTimelineMarker,
  deleteTimelineMarker,
  setShowSelectedTimeRows,
  addTimeRowIdsInGroupingProgress,
  removeTimeRowIdsInGroupingProgress,
  setDraggingTimeRowId,
  setTimesheetFields,
  setTimesheetField,
  deleteTimesheetField,
  setTimeGroupFieldConfigs,
  setTimesheetFieldConfigs,
  setTimesheetFieldOptions,
  setRecentTimesheetFieldOptions,
  setFavouriteTimesheetFieldOptions,
  setTimesheetFieldViewMode,
  setKeepTabOrder,
  removeKeepTabOrder,
} = slice.actions

export const selectTimelineState = (state: RootState): TimelinesState => state.timeline
export const selectActiveDate = (state: RootState): TimelineDate => state.timeline.activeDate
export const selectTimeRowsCache = (state: RootState): TimeRowsCache => timeRowsIndexer.cache(state.timeline)
export const selectTimeGroupsCache = (state: RootState): TimeGroupsCache => timeGroupsIndexer.cache(state.timeline)
export const selectTimesheetFieldConfigsCache = (state: RootState): TimesheetFieldConfigsCache =>
  state.timeline.timesheetFieldConfigsCache
export const selectTimesheetFieldConfigsCacheTimestamps = (state: RootState): TimesheetFieldConfigsCacheTimestamps =>
  state.timeline.timesheetFieldConfigsCacheTimestamps
export const selectPendingTimesheetFieldConfigRequests = (state: RootState): PendingTimesheetFieldConfigRequests =>
  state.timeline.pendingTimesheetFieldConfigRequests
export const selectTimeGroupFieldConfigsCache = (state: RootState): TimeGroupFieldConfigsCache =>
  state.timeline.timeGroupFieldConfigsCache
export const selectTimesheetFieldOptionsCache = (state: RootState): TimesheetFieldOptionsCache =>
  state.timeline.timesheetFieldOptionsCache
export const isTimelineCachedForDate = (state: RootState) => (date: TimelineDate): boolean =>
  isTimelineCached(date, state.timeline)
export const selectActiveTimeGroups = createSelector(
  selectActiveDate,
  selectTimelineState,
  (activeDate, state): TimeGroup.AsObject[] => {
    let activeTimeGroupsIds = undefined
    if (activeDate in state.activeTimeGroupsIdsByDate) {
      activeTimeGroupsIds = state.activeTimeGroupsIdsByDate[activeDate]
    }
    if (activeTimeGroupsIds !== undefined) {
      return activeTimeGroupsIds
        .map(activeTimeGroupId => timeGroupsIndexer.findById(activeTimeGroupId, state))
        .filter(notUndefined)
    }
    return emptyArray
  }
)

export const selectActiveTimeGroupsIdsByDate = (state: RootState): ActiveTimeGroupsIdsByDate =>
  state.timeline.activeTimeGroupsIdsByDate
export const selectActiveTimeGroupsIdsForActiveDate = (state: RootState): string[] =>
  state.timeline.activeTimeGroupsIdsByDate[state.timeline.activeDate] ?? emptyArray
export const selectActiveTimeRowIdsByDate = (state: RootState): ActiveTimeRowIdsByDate => state.timeline.activeTimeRowIdsByDate
export const selectUpdatingTimeRowsStatus = (state: RootState): UpdatingTimeRowsStatus => state.timeline.updatingTimeRowsStatus
export const selectIsLoading = (state: RootState): boolean => state.timeline.isLoading
export const selectRefreshRequired = (state: RootState): boolean => state.timeline.refreshRequired
export const selectTimeZone = (state: RootState): TimeZone | undefined => state.timeline.timeZone
export const selectTimeGroupsDisplayMode = (state: RootState): TimeGroupsDisplayMode => state.timeline.timeGroupsDisplayMode
export const selectExpandedGroupsSegments = (state: RootState): string[] => state.timeline.expandedGroupsSegments
export const selectCollapsedGroupsSegments = (state: RootState): string[] => state.timeline.collapsedGroupsSegments
export const selectCreateRowIndexesList = (state: RootState): number[] => state.timeline.createRowIndexesList
export const selectFailedTimeGroupId = (state: RootState): string | undefined => state.timeline.failedTimeGroupId
export const selectCurrentDay = (state: RootState): TimelineDate => state.timeline.currentDay
export const selectShowSelectedTimeRows = (state: RootState): boolean => state.timeline.showSelectedTimeRows
export const selectTimeRowIdsInGroupingProgress = (state: RootState): string[] => state.timeline.timeRowIdsInGroupingProgress
export const selectDraggingTimeRowId = (state: RootState): string | undefined => state.timeline.draggingTimeRowId
export const selectIsInDragState = (state: RootState): boolean => state.timeline.draggingTimeRowId !== undefined
export const selectTimesheetFieldViewMode = (state: RootState): TimesheetFieldViewMode => state.timeline.timesheetFieldViewMode

export const selectTimeRowsForActiveDate = createSelector(
  selectActiveDate,
  selectTimelineState,
  (activeDate: TimelineDate, state: TimelinesState): TimeRow.AsObject[] =>
    timeRowsIndexer.findByIndex("byDate", activeDate, state),
)
export const selectTimeGroupsForActiveDate = createSelector(
  selectActiveDate,
  selectTimelineState,
  (activeDate: TimelineDate, state: TimelinesState): TimeGroup.AsObject[] =>
    timeGroupsIndexer.findByIndex("byDate", activeDate, state),
)

export const selectTimeMarkersForActiveDate = createSelector(
  selectActiveDate,
  selectTimelineState,
  (activeDate: TimelineDate, state: TimelinesState): TimelineMarker.AsObject[] =>
    timeMarkersIndexer.findByIndex("byDate", activeDate, state),
)

export const selectTrashedTimeRowsForActiveMonth = createSelector(
  // We can't use selectActiveTrashMonth due to setTimeRowsForDates import in trash slice
  (state: RootState): TimelineMonth => state.trash.activeTrashMonth,
  selectTimelineState,
  (month, state): TimeRow.AsObject[] => R.flatten(
    monthTimelineDates(month)
      .map(date => timeRowsIndexer.findByIndex("trashedByDate", date, state))
  ),
)

export const selectTimeRowsForActiveWeek = createSelector(
  selectActiveDate,
  selectTimelineState,
  (activeDate, state): TimeRow.AsObject[] =>
    R.flatten(
      getDatesOfWeek(toDate(activeDate))
        .map(date => timeRowsIndexer.findByIndex("byDate", date, state))
    ))
/*
  Selector which depends on prop and used in multiple component instances
  https://react-redux.js.org/api/hooks#using-memoizing-selectors
 */
export const selectTimeRowsForCustomDate = () => createSelector(
  (_: RootState, date: TimelineDate) => date,
  selectTimelineState,
  (date, state): TimeRow.AsObject[] => timeRowsIndexer.findByIndex("byDate", date, state),
)

export const selectTimeRow = () => createSelector(
  (_: RootState, id: string) => id,
  selectTimelineState,
  (id, state): TimeRow.AsObject | undefined => timeRowsIndexer.findById(id, state),
)
const sortTimesheetFields = <T extends { timesheetField: { displayOrder: number }}>(field1: T, field2: T) => (
  field1.timesheetField.displayOrder - field2.timesheetField.displayOrder
)
export type ConfiguredTimesheetField = {
  timesheetField: TimesheetField.AsObject,
  timesheetFieldConfig: TimesheetFieldConfig.AsObject,
  favouriteTimesheetFieldOptions: TimesheetFieldOption.AsObject[],
  recentTimesheetFieldOptions: TimesheetFieldOption.AsObject[],
  selectedTimesheetFieldOption: SelectedTimesheetFieldOption.AsObject | undefined,
  cachedSelectedOptionLabel: string | undefined,
}
const isConfiguredTimesheetField = (result: any): result is ConfiguredTimesheetField =>
  result.timesheetFieldConfig !== undefined && result.timesheetField !== undefined
export const selectConfiguredTimesheetFieldsCombiner = (
  id: string,
  currentSiteId: SiteId | undefined,
  state: TimelinesState,
): ConfiguredTimesheetField[] => {
  const timeGroup = timeGroupsIndexer.findById(id, state)
  if (!timeGroup) {
    return emptyArray
  }
  const configHashList = state.timeGroupFieldConfigsCache[id] ?? emptyArray
  return configHashList.map(hash => {
    const timesheetFieldConfig = state.timesheetFieldConfigsCache[hash]
    const timesheetFieldId = timesheetFieldConfig?.configRef?.timesheetFieldId ?? ""
    const cacheKey = currentSiteId ? createTimesheetFieldCacheKey(currentSiteId, timesheetFieldId) : timesheetFieldId
    const timesheetField = state.timesheetFieldsCache[cacheKey]
    const selectedTimesheetFieldOption = timeGroup.selectedTimesheetFieldOptionsList.find(o => (
      o.siteId === currentSiteId && o.timesheetFieldId === timesheetField?.id
    ))
    const cachedSelectedOption = selectedTimesheetFieldOption 
      ? state.timesheetFieldOptionsCache[selectedTimesheetFieldOption.timesheetFieldOptionId] 
      : undefined
    const favouritesCacheKey = currentSiteId ? createTimesheetFieldCacheKey(currentSiteId, timesheetFieldId) : timesheetFieldId
    const recentsCacheKey = currentSiteId ? createTimesheetFieldCacheKey(currentSiteId, timesheetFieldId) : timesheetFieldId
    return ({
      timesheetField: timesheetField?.siteId === currentSiteId ? timesheetField : undefined,
      timesheetFieldConfig,
      favouriteTimesheetFieldOptions: state.favouriteTimesheetFieldOptionsByField[favouritesCacheKey] || emptyArray,
      recentTimesheetFieldOptions: state.recentTimesheetFieldOptionsByField[recentsCacheKey] || emptyArray,
      selectedTimesheetFieldOption,
      cachedSelectedOptionLabel: cachedSelectedOption?.label,
    })
  })
    .filter(isConfiguredTimesheetField)
    .sort(sortTimesheetFields)
}
export const selectConfiguredTimesheetFields = () => createSelector(
  (_: RootState, id: string) => id,
  selectCurrentSiteId,
  selectTimelineState,
  selectConfiguredTimesheetFieldsCombiner,
)
/**
 * Determines if timesheet fields are available for the current site.
 * Returns true only if:
 * 1. Current site ID is defined
 * 2. Site is not marked as having no timesheet fields
 * 3. At least one timesheet field exists in cache for the current site
 */
export const selectHasTimesheetFieldsForSite = (state: RootState) =>
  state.userContext.currentSiteId !== undefined
  && !state.timesheetFields.noTimesheetFieldsSiteIdsList.includes(state.userContext.currentSiteId)
  && Object.values(state.timeline.timesheetFieldsCache)
    .some(field => field.siteId === state.userContext.currentSiteId)
export type PostedTimesheetField = {
  timesheetField: TimesheetField.AsObject,
  timesheetFieldOption: TimesheetFieldOption.AsObject,
}
export const selectPostedTimesheetFieldsCombiner = (
  id: string,
  state: TimelinesState,
  currentSiteId: SiteId | undefined,
): PostedTimesheetField[] => {
  const timeGroup = timeGroupsIndexer.findById(id, state)
  const postedSiteId = timeGroup?.postedToSiteId || currentSiteId
  if (!timeGroup || !postedSiteId) {
    return emptyArray
  }
  return timeGroup.selectedTimesheetFieldOptionsList
    .filter(option => option.siteId === postedSiteId)
    .map(option => {
      const cacheKey = createTimesheetFieldCacheKey(postedSiteId, option.timesheetFieldId)
      const timesheetField = state.timesheetFieldsCache[cacheKey]
      const timesheetFieldOption = state.timesheetFieldOptionsCache[option.timesheetFieldOptionId]
      if (!timesheetField || !timesheetFieldOption) {
        return undefined
      }
      
      return {
        timesheetField,
        timesheetFieldOption,
      }
    })
    .filter(notUndefined)
    .sort(sortTimesheetFields)
}
export const selectPostedTimesheetFields = () => createSelector(
  (_: RootState, id: string) => id,
  selectTimelineState,
  selectCurrentSiteId,
  selectPostedTimesheetFieldsCombiner,
)

export type ConfigurableTimesheetField = {
  timesheetField: TimesheetField.AsObject,
  timesheetFieldConfig?: TimesheetFieldConfig.AsObject,
  selectedTimesheetFieldOptionId?: string,
  favouriteTimesheetFieldOptions: TimesheetFieldOption.AsObject[],
  recentTimesheetFieldOptions: TimesheetFieldOption.AsObject[],
}
export const selectConfigurableTimesheetFieldsCombiner = (siteId: string, state: RootState): Array<ConfigurableTimesheetField> => {
  const configurableTimesheetFields: ConfigurableTimesheetField[] = []

  Object.values(state.timeline.timesheetFieldsCache).forEach(timesheetField => {
    if (timesheetField.siteId === siteId) {
      const matchingConfig = Object.values(state.timesheetFields.timesheetFieldConfigs)
        .find(config => config.configRef?.timesheetFieldId === timesheetField.id)
      const timesheetFieldCacheKey = createTimesheetFieldCacheKey(siteId, timesheetField.id)

      configurableTimesheetFields.push({
        timesheetField,
        timesheetFieldConfig: matchingConfig,
        favouriteTimesheetFieldOptions: state.timeline.favouriteTimesheetFieldOptionsByField[timesheetFieldCacheKey] || emptyArray,
        recentTimesheetFieldOptions: state.timeline.recentTimesheetFieldOptionsByField[timesheetFieldCacheKey] || emptyArray,
      })
    }
  })

  return configurableTimesheetFields
    .filter(t => t.timesheetFieldConfig !== undefined)
    .sort((a, b) =>
      a.timesheetField.displayOrder - b.timesheetField.displayOrder
    )
}
export const selectConfigurableTimesheetFields = () => createSelector(
  (_: RootState, siteId: string) => siteId,
  (state: RootState) => state,
  selectConfigurableTimesheetFieldsCombiner,
)
export const selectConfigurableTimesheetFieldForTimeRowsCombiner = (
  configurableTimesheetFields: ConfigurableTimesheetField[],
): ConfigurableTimesheetField | undefined => {
  // Return undefined when there are 0 or more than 1 configurable timesheet fields
  // Only return the field when there's exactly 1
  return configurableTimesheetFields.length === 1 ? configurableTimesheetFields[0] : undefined
}
export const selectConfigurableTimesheetFieldForTimeRows = () => createSelector(
  selectConfigurableTimesheetFields(),
  selectConfigurableTimesheetFieldForTimeRowsCombiner,
)
export const selectTimesheetFieldOption = () => createSelector(
  (_: RootState, timesheetFieldOptionId: string) => timesheetFieldOptionId,
  selectTimesheetFieldOptionsCache,
  (
    timesheetFieldOptionId: string,
    timesheetFieldOptionsCache: TimesheetFieldOptionsCache,
  ): TimesheetFieldOption.AsObject | undefined => timesheetFieldOptionsCache[timesheetFieldOptionId],
)
export const selectKeepTabOrderCombiner = (
  timeGroupId: string,
  state: TimelinesState,
): Cell | undefined => state.keepTabOrder[timeGroupId]
export const selectKeepTabOrder = () => createSelector(
  (_: RootState, timeGroupId: string) => timeGroupId,
  selectTimelineState,
  selectKeepTabOrderCombiner,
)

export const selectActiveTimeRows = createSelector(
  selectActiveDate,
  selectActiveTimeRowIdsByDate,
  selectTimelineState,
  (
    activeDate,
    activeTimeRowIdsByDate,
    state,
  ): TimeRow.AsObject[] =>
    timeRowsIndexer
      .findByIndex("byDate", activeDate, state)
      .filter(timeRow => (activeTimeRowIdsByDate[activeDate] ?? []).includes(timeRow.id))
)

export const selectActiveTimeRowsIds = createSelector(
  selectActiveTimeRows,
  (timeRows: TimeRow.AsObject[]): string[] => timeRows.map(t => t.id)
)

export const selectTimeBlocks = createSelector(
  selectTimeRowsForActiveDate,
  selectTimeMarkersForActiveDate,
  selectTimeZone,
  selectToolbarState,
  selectCurrentSiteId,
  selectShowSelectedTimeRows,
  selectActiveTimeRowsIds,
  (
    timeRowsForDate,
    timeMarkersForDate,
    timeZone,
    toolbarState,
    currentSiteId,
    showSelectedTimeRows,
    activeTimeRowIds,
  ): TimeBlock[] => {
    if (!timeZone) {
      return emptyArray
    }
    return getTimeBlocks(
      timeRowsForDate.filter(timeRow => timeRow.timeGroupId === ""),
      timeZone.name,
      { toolbarState, currentSiteId, showSelectedTimeRows },
      timeMarkersForDate,
      activeTimeRowIds,
    )
  }
)

export const selectTimeRowsCombiner = (
  timeRowsForDate: TimeRow.AsObject[],
  toolbarState: ToolbarState,
  currentSiteId: SiteId | undefined,
  showSelectedTimeRows: boolean,
  activeTimeRowIds: TimeRowId[],
): TimeRow.AsObject[] => (
  timeRowsForDate
    .reduce((result: TimeRow.AsObject[], timeRow: TimeRow.AsObject) => {
      if (timeRow.timeGroupId === ""
        && timeRow.trashStatus === TrashStatus.NOT_TRASHED
        && isMatchingToolbarAndSearchFilters(timeRow, toolbarState, currentSiteId)
        && isMatchingShowSelectedItemToggle(timeRow, showSelectedTimeRows, activeTimeRowIds)) {
        return result.concat(({
          ...timeRow,
          tagsList: timeRow.tagsList.filter(t => t.siteId === currentSiteId),
        }))
      }
      return result
    }, [])
    .sort((timeRow1, timeRow2) =>
      sortTypeStrategies[toolbarState.sortType](timeRow1, timeRow2)
    )
)

export const selectTimeRows = createSelector(
  selectTimeRowsForActiveDate,
  selectToolbarState,
  selectCurrentSiteId,
  selectShowSelectedTimeRows,
  selectActiveTimeRowsIds,
  selectTimeRowsCombiner,
)

export const selectSearchSuggestions = () => createSelector(
  (_: RootState, inputValue: string) => inputValue,
  selectTimeRowsForActiveDate,
  selectTimeGroupsCache,
  selectTagsCache,
  selectTagCategoriesCache,
  selectCurrentSiteId,
  selectSearchTerms,
  searchSuggestionsCombiner,
)

export const collapsibleTimeGroupsCombiner = (
  timeGroups: TimeGroup.AsObject[],
  timeRows: TimeRow.AsObject[],
  toolbar: ToolbarState,
  currentSiteId: SiteId | undefined,
): CollapsibleTimeGroup[] =>
  timeGroups.map(timeGroup => ({
    timeGroup,
    timeRows: timeRows.filter(timeRow => timeRow.timeGroupId === timeGroup.id),
  })).filter(collapsibleTimeGroup => isMatchingToolbarAndSearchFilters(
    collapsibleTimeGroup,
    toolbar,
    currentSiteId,
  )).sort((a, b) =>
    sortTimeGroupsByCreateTime(a.timeGroup, b.timeGroup)
  )

export const selectCollapsibleTimeGroups = createSelector(
  selectTimeGroupsForActiveDate,
  selectTimeRowsForActiveDate,
  selectToolbarState,
  selectCurrentSiteId,
  collapsibleTimeGroupsCombiner,
)

/*
  Selector which depends on prop and used in multiple component instances
  https://react-redux.js.org/api/hooks#using-memoizing-selectors
 */
export const selectCollapsibleTimeGroupsCountForCustomDate = () => createSelector(
  (_: RootState, date: TimelineDate) => date,
  selectTimelineState,
  selectToolbarState,
  selectCurrentSiteId,
  (date, state, toolbar, currentSiteId): number => {
    const timeGroups = timeGroupsIndexer.findByIndex("byDate", date, state)
    const timeRows = timeRowsIndexer.findByIndex("byDate", date, state)
    return collapsibleTimeGroupsCombiner(timeGroups, timeRows, toolbar, currentSiteId).length
  },
)

export const selectUnpostedUntrashedTimeRowsCount = createSelector(
  selectTimeRowsForActiveDate,
  countUnpostedUntrashedUngroupedRows,
)

export const selectUntrashedTimeRowsCount = createSelector(
  selectTimeRowsForActiveDate,
  countUntrashedRows,
)

export const selectTrashedTimeRowsCount = createSelector(
  selectTimeRowsForActiveDate,
  countTrashedRows,
)

export const availableTimeGroups = (timeGroups: CollapsibleTimeGroup[]): TimeGroup.AsObject[] =>
  timeGroups
    .filter(item => item.timeGroup.postedToSiteId === "")
    .map(item => item.timeGroup)
export const selectAvailableTimeGroups = createSelector(
  selectCollapsibleTimeGroups,
  availableTimeGroups,
)

export const selectHasTimeGroupsForDate = createSelector(
  selectAvailableTimeGroups,
  (timeGroups: TimeGroup.AsObject[]): boolean => timeGroups.length > 0
)

export const selectIsPostingSelection = createSelector(
  selectIsPostingTime,
  selectActiveTimeGroups,
  (isPosting: boolean, timeGroups: TimeGroup.AsObject[]): boolean => (
    !isPosting && timeGroups.length > 0 && timeGroups.every(isPostingTime)
  )
)

export const selectActiveDateTotalDurationSecs = createSelector(
  selectTimeRowsForActiveDate,
  (timeRowsForActiveDate: TimeRow.AsObject[]): number => (
    getTimeRowsDurationSecs(timeRowsForActiveDate)
  )
)

const uniqueTagIds = (rows: TimeRow.AsObject[], groups: TimeGroup.AsObject[]): TagId[] => {
  const timeRowTagIds = rows.flatMap(r => r.tagsList).map(tag => tag.id)
  const timeRowSuggestedTagIds = rows.flatMap(r => r.tagSuggestionsList).map(ts => ts.tag?.id ?? ts.id)
  const timeGroupTagIds = groups.flatMap(g => g.tagsList).map(tag => tag.id)
  return R.uniq(timeRowTagIds.concat(timeRowSuggestedTagIds).concat(timeGroupTagIds))
}

const selectActiveDateTagColors = createSelector(
  selectTimeRowsForActiveDate,
  selectTimeGroupsForActiveDate,
  (timeRows, timeGroups): ColorAssignment[] =>
    R.pipe(
      uniqueTagIds,
      assignColorsSorted,
      modifySimilarColors,
    )(timeRows, timeGroups)
)

export const selectTagColor = () => createSelector(
  (_: RootState, tagId: TagId) => tagId,
  selectActiveDateTagColors,
  (tagId, activeDateTagColors): HexColor => {
    const color = activeDateTagColors.find(ca => ca.id === tagId)?.color ?? colorFromId(tagId)
    return color.hex
  }
)

export const selectTaggedTimeRowsWithDifferentAssignee = () => createSelector(
  (_: RootState, timeRows: TimeRow.AsObject[]) => timeRows,
  selectTagsCache,
  selectCurrentSiteId,
  selectCurrentExternalId,
  selectUserEmail,
  (
    timeRows,
    tagsCache,
    currentSiteId,
    currentExternalId,
    currentUserEmail,
  ) => {
    const tags: TagRef.AsObject[] = R.flatten(
      timeRows.map(row => [...row.tagsList, ...row.tagSuggestionsList.map(t => t.tag).filter(notUndefined)])
    )
    return hasDifferentTagAssignee({
      tagsList: tags,
      tagsCache,
      currentSiteId,
      currentExternalId,
      currentUserEmail
    })
  }
)

export const selectTimeGroupHasTagWithDifferentAssignee = () => createSelector(
  (_: RootState, timeGroupId: string) => timeGroupId,
  selectAvailableTimeGroups,
  selectTagsCache,
  selectCurrentSiteId,
  selectCurrentExternalId,
  selectUserEmail,
  (
    timeGroupId,
    availableTimeGroups,
    tagsCache,
    currentSiteId,
    currentExternalId,
    currentUserEmail,
  ) => {
    const timeGroup: TimeGroup.AsObject | undefined = availableTimeGroups.find(t => t.id === timeGroupId)
    return timeGroup !== undefined
      && hasDifferentTagAssignee({
        tagsList: timeGroup.tagsList,
        tagsCache,
        currentSiteId,
        currentExternalId,
        currentUserEmail
      })
  },
)

export const selectActiveUnpostedTimeGroupsHaveTagWithDifferentAssignee = createSelector(
  selectAvailableTimeGroups,
  selectActiveTimeGroupsIdsForActiveDate,
  selectTagsCache,
  selectCurrentSiteId,
  selectCurrentExternalId,
  selectUserEmail,
  (
    availableTimeGroups,
    activeTimeGroupsIdsForActiveDate,
    tagsCache,
    currentSiteId,
    currentExternalId,
    currentUserEmail,
  ) => availableTimeGroups
    .filter(item => activeTimeGroupsIdsForActiveDate.includes(item.id))
    .some(t => hasDifferentTagAssignee({
      tagsList: t.tagsList,
      tagsCache,
      currentSiteId,
      currentExternalId,
      currentUserEmail
    }))
)

export default slice.reducer
